import React from 'react';
import { useTranslation } from 'react-i18next';
import { alpha } from '@mui/material/styles';
import {
  Box, Stack, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Typography,
} from '@mui/material';

import { dataTableSx, HeadLabelCell } from '../../../shared/components/TableHeader';
import { formatPercent, formatTonne } from '../../../shared/utils/number';
import { MONO_FONT, SCOPE_TOKENS, tableTokens } from '../../../theme/muiTheme';

const ROW_TONES = {
  scope1: SCOPE_TOKENS.SCOPE_1,
  scope2: SCOPE_TOKENS.SCOPE_2,
  scope3: SCOPE_TOKENS.SCOPE_3,
};

const resolveLabel = (row, t) => {
  const text = row.labelKey ? t(row.labelKey) : row.label;
  return row.catPrefix ? `${row.catPrefix}: ${text}` : text;
};

export default function ReportBreakdownTable({ rows, total, totalPercentage }) {
  const { t } = useTranslation();

  return (
    <TableContainer sx={{ maxHeight: 640, overflow: 'auto' }}>
      <Table
        size="small"
        aria-label={t('reports.table.ariaLabel')}
        sx={(theme) => ({ ...dataTableSx(theme), minWidth: 720 })}
      >
        <TableHead sx={{ position: 'sticky', top: 0, zIndex: 2, bgcolor: 'background.paper' }}>
          <TableRow>
            <HeadLabelCell>{t('reports.table.category')}</HeadLabelCell>
            <HeadLabelCell align="right" width={180}>{t('reports.table.total')}</HeadLabelCell>
            <HeadLabelCell align="right" width={150}>{t('reports.table.percentage')}</HeadLabelCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => {
            const tone = ROW_TONES[row.tone] || SCOPE_TOKENS.SCOPE_3;
            const isScope = row.kind === 'scope';
            return (
              <TableRow
                key={row.id}
                sx={(theme) => ({
                  bgcolor: isScope ? alpha(tone.color, theme.palette.mode === 'dark' ? 0.13 : 0.06) : 'transparent',
                })}
              >
                <TableCell sx={{ pl: 2 + (row.level * 2.25) }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    {isScope && (
                      <Box sx={{ bgcolor: tone.color, borderRadius: '2px', flexShrink: 0, height: 9, width: 9 }} />
                    )}
                    <Typography
                      variant="body2"
                      sx={(theme) => ({
                        color: isScope
                          ? (theme.palette.mode === 'dark' ? tone.color : tone.dark)
                          : 'text.primary',
                        fontWeight: isScope ? 800 : 600,
                      })}
                    >
                      {resolveLabel(row, t)}
                    </Typography>
                  </Stack>
                </TableCell>
                <TableCell
                  align="right"
                  sx={(theme) => ({
                    color: isScope
                      ? (theme.palette.mode === 'dark' ? tone.color : tone.dark)
                      : 'text.primary',
                    fontFamily: MONO_FONT,
                    fontWeight: isScope ? 800 : 600,
                  })}
                >
                  {formatTonne(row.value)}
                </TableCell>
                <TableCell
                  align="right"
                  sx={(theme) => ({
                    color: isScope
                      ? (theme.palette.mode === 'dark' ? tone.color : tone.dark)
                      : 'text.secondary',
                    fontFamily: MONO_FONT,
                    fontWeight: isScope ? 800 : 600,
                  })}
                >
                  {formatPercent(row.percentage)}
                </TableCell>
              </TableRow>
            );
          })}
          <TableRow
            sx={(theme) => ({
              position: 'sticky',
              bottom: 0,
              zIndex: 2,
              bgcolor: tableTokens(theme).headBg,
              boxShadow: `0 -1px 0 ${tableTokens(theme).headRule}`,
            })}
          >
            <TableCell sx={{ fontWeight: 850 }}>{t('reports.table.grandTotal')}</TableCell>
            <TableCell align="right" sx={{ fontFamily: MONO_FONT, fontWeight: 850 }}>
              {formatTonne(total)}
            </TableCell>
            <TableCell align="right" sx={{ fontFamily: MONO_FONT, fontWeight: 850 }}>
              {formatPercent(totalPercentage)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </TableContainer>
  );
}